import { LaneAllocator } from 'features/room-reactions/utils/LaneAllocator';

export type LaneDrainEntry<T> = {
  item: T;
  lane: number;
  delayMs: number;
};

export type LaneDrainBatchHandler<T> = (batch: ReadonlyArray<LaneDrainEntry<T>>) => void;

/**
 * Buffers items and releases them through a `LaneAllocator`: each drain pass
 * consumes items while lanes are available, then schedules a retry for when
 * the next lane becomes eligible. Callers receive batches ready for render.
 */
export class LaneDrainQueue<T> {
  private queue: T[] = [];

  private timer: number | null = null;

  constructor(
    private readonly allocator: LaneAllocator,
    private readonly onBatch: LaneDrainBatchHandler<T>
  ) {}

  push(items: readonly T[]): void {
    if (items.length === 0) {
      return;
    }
    this.queue.push(...items);
    this.drain();
  }

  clear(): void {
    this.queue.length = 0;
    this.cancelTimer();
  }

  private drain = (): void => {
    this.timer = null;

    const batch: LaneDrainEntry<T>[] = [];
    while (this.queue.length > 0) {
      const allocation = this.allocator.tryAllocate(Date.now());
      if (!allocation) {
        break;
      }
      const item = this.queue.shift();
      if (!item) {
        break;
      }
      batch.push({ item, lane: allocation.lane, delayMs: allocation.delayMs });
    }

    if (batch.length > 0) {
      this.onBatch(batch);
    }

    if (this.queue.length > 0) {
      const wait = Math.max(1, this.allocator.nextEligibleAt() - Date.now());
      this.timer = window.setTimeout(this.drain, wait);
    }
  };

  private cancelTimer(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
